import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface CalendarDay {
    iso: string;
    day: number;
    inMonth: boolean;
    isToday: boolean;
}

/** Selector de fecha único tipo calendario (mismo patrón visual que
 * app-date-range-picker de Centro de inteligencia operativa, pero para un
 * solo día): un "pill" con la fecha formateada que despliega un calendario
 * visual al hacer click, en vez de un &lt;input type="date"&gt; nativo. Al
 * elegir un día se aplica de inmediato (sin botón "Aplicar" aparte) y se
 * cierra, para que quien escuche el cambio (p. ej. el recálculo de la tabla
 * de vacaciones) reaccione al instante.
 *
 * Implementa ControlValueAccessor: se usa igual que un input nativo, con
 * formControlName="..." o [(ngModel)]="...". */
@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.css'],
    host: { '(document:click)': 'onDocumentClick($event)' },
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DatePickerComponent),
        multi: true
    }]
})
export class DatePickerComponent implements ControlValueAccessor {
    @Input() placeholder = 'Seleccionar fecha';
    @Output() dateChange = new EventEmitter<string>();

    readonly weekdayLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    readonly monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: this.capitalize(new Intl.DateTimeFormat('es-MX', { month: 'long' }).format(new Date(2000, i, 1)))
    }));
    /** Rango generoso: cubre desde fechas de nacimiento (colaboradores) hasta
     * vencimientos de contrato a futuro, sin acotar de más ningún campo. */
    readonly yearOptions = Array.from({ length: 111 }, (_, i) => new Date().getFullYear() + 10 - i);

    value = '';
    disabled = false;
    open = false;
    viewYear = new Date().getFullYear();
    viewMonth = new Date().getMonth();

    /** Array normal (NO getter): un getter referenciado desde *ngFor se
     * reevalúa en cada ciclo de detección de cambios y *ngFor ve 42 objetos
     * "nuevos" cada vez, destruyendo/recreando los botones sin parar — se
     * recalcula solo cuando realmente cambia el mes (ver date-range-picker). */
    calendarDays: CalendarDay[] = [];

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    get label(): string {
        if (!this.value) return this.placeholder;
        return this.formatDate(this.value);
    }

    private capitalize(text: string): string {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    writeValue(value: string): void {
        this.value = value || '';
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private buildCalendarDays(): void {
        const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
        // Lunes=0 ... Domingo=6, para empezar la grilla en lunes.
        const leadingBlank = (firstOfMonth.getDay() + 6) % 7;
        const gridStart = new Date(this.viewYear, this.viewMonth, 1 - leadingBlank);

        const todayIso = this.toIso(new Date());
        const days: CalendarDay[] = [];
        for (let i = 0; i < 42; i++) {
            const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
            const iso = this.toIso(date);
            days.push({
                iso,
                day: date.getDate(),
                inMonth: date.getMonth() === this.viewMonth,
                isToday: iso === todayIso,
            });
        }
        this.calendarDays = days;
    }

    trackByIso(_index: number, day: CalendarDay): string {
        return day.iso;
    }

    private toIso(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    private formatDate(value: string): string {
        const date = new Date(value + 'T00:00:00');
        if (isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    }

    toggle(): void {
        if (this.disabled) return;
        if (!this.open) {
            const base = this.value ? new Date(this.value + 'T00:00:00') : new Date();
            this.viewYear = base.getFullYear();
            this.viewMonth = base.getMonth();
            this.buildCalendarDays();
        }
        this.open = !this.open;
        if (!this.open) this.onTouched();
    }

    prevMonth(): void {
        this.viewMonth--;
        if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear--; }
        this.buildCalendarDays();
    }

    nextMonth(): void {
        this.viewMonth++;
        if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++; }
        this.buildCalendarDays();
    }

    /** Saltar directo a un mes/año (selects), en vez de navegar de a un mes
     * con las flechas — imprescindible para fechas lejanas como el
     * nacimiento de un colaborador. */
    onMonthSelect(month: string): void {
        this.viewMonth = Number(month);
        this.buildCalendarDays();
    }

    onYearSelect(year: string): void {
        this.viewYear = Number(year);
        this.buildCalendarDays();
    }

    selectDay(iso: string): void {
        this.value = iso;
        this.onChange(iso);
        this.dateChange.emit(iso);
        this.open = false;
        this.onTouched();
    }

    clear(event: Event): void {
        event.stopPropagation();
        this.value = '';
        this.onChange('');
        this.dateChange.emit('');
    }

    onDocumentClick(event: MouseEvent): void {
        if (!this.open) return;
        const target = event.target as HTMLElement;
        if (!target.closest('app-date-picker')) {
            this.open = false;
            this.onTouched();
        }
    }
}
